import { useState, useEffect, useRef } from 'react';
import { ChevronLeft, Plus, Trash2, Edit2, Eye, EyeOff, MapPin, Download, Upload } from 'lucide-react';
import { MapContainer, TileLayer, Polyline, Marker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { useAuthStore } from '../../stores';
import { api } from '../../utils/api';

// Component to fit map bounds to geometry
function FitBounds({ geometry }) {
  const map = useMap();
  
  useEffect(() => {
    if (!geometry || !geometry.coordinates) return;
    
    try {
      let bounds;
      if (geometry.type === 'Point') {
        map.setView(geometry.coordinates.reverse(), 13);
      } else if (geometry.type === 'LineString') {
        bounds = geometry.coordinates.map(coord => [coord[1], coord[0]]);
        map.fitBounds(bounds, { padding: [50, 50] });
      } else if (geometry.type === 'Polygon') {
        bounds = geometry.coordinates[0].map(coord => [coord[1], coord[0]]);
        map.fitBounds(bounds, { padding: [50, 50] });
      }
    } catch (error) {
      console.error('Error fitting bounds:', error);
    }
  }, [geometry, map]);
  
  return null;
}

export default function AdminEventLayers({ event, onBack }) {
  const { token } = useAuthStore();
  const [layers, setLayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingLayer, setEditingLayer] = useState(null);
  const [showMapPreview, setShowMapPreview] = useState(false);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    layer_type: 'route',
    geometry: '',
    icon_url: '',
    icon_type: 'emoji', // 'emoji', 'url', 'fontawesome', 'flaticon'
    style: {
      color: '#3388ff',
      weight: 3,
      opacity: 0.8,
      dashArray: '', // Line pattern (e.g., '5, 10' for dashed)
      lineCap: 'round', // 'butt', 'round', 'square'
      lineJoin: 'round', // 'miter', 'round', 'bevel'
      fillColor: '#3388ff',
      fillOpacity: 0.2
    },
    metadata: {},
    display_order: 1,
    is_visible: true
  });

  useEffect(() => {
    loadLayers();
  }, [event.id]);

  const loadLayers = async () => {
    try {
      setLoading(true);
      const response = await api.getEvent(event.slug || event.id);
      setLayers(response.event?.layers || []);
    } catch (error) {
      console.error('Error loading layers:', error);
      alert('Σφάλμα φόρτωσης layers');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      layer_type: 'route',
      geometry: '',
      icon_url: '',
      icon_type: 'emoji',
      style: {
        color: '#3388ff',
        weight: 3,
        opacity: 0.8,
        dashArray: '',
        lineCap: 'round',
        lineJoin: 'round',
        fillColor: '#3388ff',
        fillOpacity: 0.2
      },
      metadata: {},
      display_order: layers.length + 1,
      is_visible: true
    });
    setEditingLayer(null);
    setShowForm(false);
    setShowMapPreview(false);
  };

  const handleEdit = (layer) => {
    // Detect icon type
    let iconType = 'emoji';
    if (layer.icon_url) {
      if (layer.icon_url.startsWith('http')) {
        iconType = 'url';
      } else if (layer.icon_url.startsWith('fa-') || layer.icon_url.startsWith('fas ') || layer.icon_url.startsWith('far ')) {
        iconType = 'fontawesome';
      } else if (layer.icon_url.startsWith('fi-')) {
        iconType = 'flaticon';
      }
    }
    
    setFormData({
      name: layer.name,
      layer_type: layer.layer_type,
      geometry: JSON.stringify(layer.geometry, null, 2),
      icon_url: layer.icon_url || '',
      icon_type: iconType,
      style: layer.style,
      metadata: typeof layer.metadata === 'string' ? layer.metadata : JSON.stringify(layer.metadata, null, 2),
      display_order: layer.display_order,
      is_visible: layer.is_visible
    });
    setEditingLayer(layer);
    setShowForm(true);
  };

  // Export layer as GeoJSON
  const handleExportGeoJSON = (layer) => {
    const geoJSON = {
      type: "Feature",
      properties: {
        name: layer.name,
        layer_type: layer.layer_type,
        style: layer.style,
        metadata: layer.metadata,
        display_order: layer.display_order,
        is_visible: layer.is_visible
      },
      geometry: layer.geometry
    };
    
    const blob = new Blob([JSON.stringify(geoJSON, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${layer.name.replace(/\s+/g, '_')}.geojson`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Import GeoJSON file
  const handleImportGeoJSON = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const geoJSON = JSON.parse(event.target.result);
        
        // Handle Feature or FeatureCollection
        if (geoJSON.type === 'Feature') {
          setFormData({
            ...formData,
            name: geoJSON.properties?.name || formData.name,
            layer_type: geoJSON.properties?.layer_type || formData.layer_type,
            geometry: JSON.stringify(geoJSON.geometry, null, 2),
            style: geoJSON.properties?.style || formData.style,
            metadata: geoJSON.properties?.metadata || formData.metadata,
            display_order: geoJSON.properties?.display_order || formData.display_order,
            is_visible: geoJSON.properties?.is_visible !== undefined ? geoJSON.properties.is_visible : formData.is_visible
          });
        } else if (geoJSON.type === 'FeatureCollection' && geoJSON.features.length > 0) {
          const feature = geoJSON.features[0];
          setFormData({
            ...formData,
            name: feature.properties?.name || formData.name,
            geometry: JSON.stringify(feature.geometry, null, 2),
            style: feature.properties?.style || formData.style,
            metadata: feature.properties?.metadata || formData.metadata
          });
        } else {
          // Assume it's just a geometry
          setFormData({
            ...formData,
            geometry: JSON.stringify(geoJSON, null, 2)
          });
        }
        
        alert('GeoJSON imported successfully!');
      } catch (error) {
        console.error('Error parsing GeoJSON:', error);
        alert('Invalid GeoJSON file: ' + error.message);
      }
    };
    reader.readAsText(file);
  };

  // Parse geometry for preview
  const getPreviewGeometry = () => {
    try {
      if (!formData.geometry) return null;
      return typeof formData.geometry === 'string' 
        ? JSON.parse(formData.geometry) 
        : formData.geometry;
    } catch (error) {
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.geometry) {
      alert('Παρακαλώ συμπληρώστε όνομα και geometry');
      return;
    }

    try {
      // Parse geometry and metadata
      const geometry = typeof formData.geometry === 'string' 
        ? JSON.parse(formData.geometry) 
        : formData.geometry;

      const metadata = typeof formData.metadata === 'string' 
        ? JSON.parse(formData.metadata) 
        : formData.metadata;

      const layerData = {
        event_id: event.id,
        name: formData.name,
        layer_type: formData.layer_type,
        geometry,
        icon_url: formData.icon_url || null,
        style: formData.style,
        metadata,
        display_order: parseInt(formData.display_order),
        is_visible: formData.is_visible
      };

      if (editingLayer) {
        await api.adminUpdateEventLayer(token, editingLayer.id, layerData);
        alert('Το layer ενημερώθηκε επιτυχώς!');
      } else {
        await api.adminCreateEventLayer(token, layerData);
        alert('Το layer δημιουργήθηκε επιτυχώς!');
      }

      resetForm();
      loadLayers();
    } catch (error) {
      console.error('Error saving layer:', error);
      alert('Σφάλμα αποθήκευσης layer: ' + error.message);
    }
  };

  const handleDelete = async (layer) => {
    if (!confirm(`Είστε σίγουροι ότι θέλετε να διαγράψετε το layer "${layer.name}";`)) {
      return;
    }

    try {
      await api.adminDeleteEventLayer(token, layer.id);
      alert('Το layer διαγράφηκε επιτυχώς!');
      loadLayers();
    } catch (error) {
      console.error('Error deleting layer:', error);
      alert('Σφάλμα διαγραφής layer: ' + error.message);
    }
  };

  const toggleVisibility = async (layer) => {
    try {
      await api.adminUpdateEventLayer(token, layer.id, {
        is_visible: !layer.is_visible
      });
      loadLayers();
    } catch (error) {
      console.error('Error toggling visibility:', error);
      alert('Σφάλμα αλλαγής visibility');
    }
  };

  const parseGeoJSON = (text) => {
    try {
      const parsed = JSON.parse(text);
      setFormData({ ...formData, geometry: JSON.stringify(parsed, null, 2) });
    } catch (error) {
      console.error('Invalid GeoJSON:', error);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={onBack}
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-900"
          >
            <ChevronLeft size={20} />
            <span>Πίσω</span>
          </button>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              Layers: {event.name}
            </h2>
            <p className="text-gray-600 mt-1">
              Διαχείριση επιπέδων χάρτη (routes, zones, polygons)
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition flex items-center space-x-2"
        >
          {showForm ? (
            <>
              <span>✕</span>
              <span>Κλείσιμο</span>
            </>
          ) : (
            <>
              <Plus size={20} />
              <span>Νέο Layer</span>
            </>
          )}
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h3 className="text-xl font-bold mb-4">
            {editingLayer ? 'Επεξεργασία Layer' : 'Νέο Layer'}
          </h3>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Όνομα Layer *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="π.χ. Λεωφορεία από Αθήνα"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Τύπος Layer
                </label>
                <select
                  value={formData.layer_type}
                  onChange={(e) => setFormData({ ...formData, layer_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="route">Route (LineString)</option>
                  <option value="zone">Zone (Polygon)</option>
                  <option value="polygon">Polygon</option>
                  <option value="line">Line</option>
                  <option value="point">Point</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Χρώμα
                </label>
                <div className="flex space-x-2">
                  <input
                    type="color"
                    value={formData.style.color}
                    onChange={(e) => setFormData({
                      ...formData,
                      style: { ...formData.style, color: e.target.value }
                    })}
                    className="w-16 h-10 rounded border border-gray-300"
                  />
                  <input
                    type="text"
                    value={formData.style.color}
                    onChange={(e) => setFormData({
                      ...formData,
                      style: { ...formData.style, color: e.target.value }
                    })}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="#3388ff"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Πάχος (Weight)
                </label>
                <input
                  type="number"
                  value={formData.style.weight}
                  onChange={(e) => setFormData({
                    ...formData,
                    style: { ...formData.style, weight: parseInt(e.target.value) }
                  })}
                  min="1"
                  max="20"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Διαφάνεια (Opacity)
                </label>
                <input
                  type="number"
                  value={formData.style.opacity}
                  onChange={(e) => setFormData({
                    ...formData,
                    style: { ...formData.style, opacity: parseFloat(e.target.value) }
                  })}
                  min="0"
                  max="1"
                  step="0.1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Line Pattern (Dash Array) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Μοτίβο Γραμμής (Pattern)
                </label>
                <select
                  value={formData.style.dashArray || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    style: { ...formData.style, dashArray: e.target.value }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Συνεχής (Solid)</option>
                  <option value="5, 5">Διακεκομμένη - Κανονική (Dashed)</option>
                  <option value="10, 10">Διακεκομμένη - Μεγάλη (Long Dash)</option>
                  <option value="2, 5">Διακεκομμένη - Μικρή (Short Dash)</option>
                  <option value="5, 10">Διακεκομμένη - Μεσαία (Medium Dash)</option>
                  <option value="1, 5">Τελεία (Dotted)</option>
                  <option value="10, 5, 2, 5">Παύλα-Τελεία (Dash-Dot)</option>
                  <option value="10, 5, 2, 5, 2, 5">Παύλα-Διπλή Τελεία (Dash-Dot-Dot)</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Επιλέξτε μοτίβο για τη γραμμή. Κενό = συνεχής γραμμή.
                </p>
              </div>

              {/* Custom Dash Array */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Προσαρμοσμένο Μοτίβο (Custom Pattern)
                </label>
                <input
                  type="text"
                  value={formData.style.dashArray || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    style: { ...formData.style, dashArray: e.target.value }
                  })}
                  placeholder="π.χ. 5, 10 ή 10, 5, 2, 5"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Εισάγετε αριθμούς χωρισμένους με κόμμα (π.χ. "5, 10" για παύλα 5px, κενό 10px)
                </p>
              </div>

              {/* Line Cap Style */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Στυλ Άκρων Γραμμής (Line Caps)
                </label>
                <select
                  value={formData.style.lineCap || 'round'}
                  onChange={(e) => setFormData({
                    ...formData,
                    style: { ...formData.style, lineCap: e.target.value }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="butt">Ίσιο (Butt) - Κομμένα άκρα</option>
                  <option value="round">Στρογγυλό (Round) - Στρογγυλεμένα άκρα</option>
                  <option value="square">Τετράγωνο (Square) - Τετράγωνα άκρα</option>
                </select>
                <div className="mt-2 flex gap-2 items-center">
                  <div className="flex-1 h-1 bg-gray-300" style={{ borderRadius: formData.style.lineCap === 'round' ? '50%' : '0' }}></div>
                  <span className="text-xs text-gray-500">Προεπισκόπηση</span>
                </div>
              </div>

              {/* Line Join Style */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Στυλ Συνδέσεων Γραμμής (Line Joins)
                </label>
                <select
                  value={formData.style.lineJoin || 'round'}
                  onChange={(e) => setFormData({
                    ...formData,
                    style: { ...formData.style, lineJoin: e.target.value }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="miter">Αιχμηρό (Miter) - Γωνίες με αιχμές</option>
                  <option value="round">Στρογγυλό (Round) - Στρογγυλεμένες γωνίες</option>
                  <option value="bevel">Λοξό (Bevel) - Κομμένες γωνίες</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Καθορίζει πώς συνδέονται οι γωνίες της γραμμής
                </p>
              </div>

              {/* Fill Color (for polygons) */}
              {formData.layer_type === 'polygon' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Χρώμα Γεμίσματος (Fill Color)
                    </label>
                    <div className="flex space-x-2">
                      <input
                        type="color"
                        value={formData.style.fillColor || formData.style.color}
                        onChange={(e) => setFormData({
                          ...formData,
                          style: { ...formData.style, fillColor: e.target.value }
                        })}
                        className="w-16 h-10 rounded border border-gray-300"
                      />
                      <input
                        type="text"
                        value={formData.style.fillColor || formData.style.color}
                        onChange={(e) => setFormData({
                          ...formData,
                          style: { ...formData.style, fillColor: e.target.value }
                        })}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="#3388ff"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Διαφάνεια Γεμίσματος (Fill Opacity)
                    </label>
                    <input
                      type="number"
                      value={formData.style.fillOpacity || 0.2}
                      onChange={(e) => setFormData({
                        ...formData,
                        style: { ...formData.style, fillOpacity: parseFloat(e.target.value) }
                      })}
                      min="0"
                      max="1"
                      step="0.1"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </>
              )}

              {/* Style Preview */}
              <div className="col-span-2 border-t pt-4">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Προεπισκόπηση Στυλ (Style Preview)
                </label>
                <div className="bg-white p-6 rounded-lg border-2 border-gray-200">
                  {formData.layer_type === 'route' && (
                    <svg width="100%" height="60" className="overflow-visible">
                      <line
                        x1="20"
                        y1="30"
                        x2="380"
                        y2="30"
                        stroke={formData.style.color}
                        strokeWidth={formData.style.weight}
                        strokeOpacity={formData.style.opacity}
                        strokeDasharray={formData.style.dashArray || 'none'}
                        strokeLinecap={formData.style.lineCap || 'round'}
                        strokeLinejoin={formData.style.lineJoin || 'round'}
                      />
                      {/* Add a corner to show lineJoin */}
                      <polyline
                        points="400,30 450,30 450,50"
                        fill="none"
                        stroke={formData.style.color}
                        strokeWidth={formData.style.weight}
                        strokeOpacity={formData.style.opacity}
                        strokeDasharray={formData.style.dashArray || 'none'}
                        strokeLinecap={formData.style.lineCap || 'round'}
                        strokeLinejoin={formData.style.lineJoin || 'round'}
                      />
                    </svg>
                  )}
                  {formData.layer_type === 'polygon' && (
                    <svg width="100%" height="100" className="overflow-visible">
                      <polygon
                        points="50,20 150,20 180,80 20,80"
                        fill={formData.style.fillColor || formData.style.color}
                        fillOpacity={formData.style.fillOpacity || 0.2}
                        stroke={formData.style.color}
                        strokeWidth={formData.style.weight}
                        strokeOpacity={formData.style.opacity}
                        strokeDasharray={formData.style.dashArray || 'none'}
                        strokeLinecap={formData.style.lineCap || 'round'}
                        strokeLinejoin={formData.style.lineJoin || 'round'}
                      />
                    </svg>
                  )}
                  {formData.layer_type === 'point' && (
                    <div className="flex items-center justify-center h-16">
                      <div className="text-4xl">
                        {formData.icon_url && formData.icon_url.startsWith('fa-') ? (
                          <i className={formData.icon_url} style={{ color: formData.style.color }}></i>
                        ) : formData.icon_url && formData.icon_url.startsWith('fi-') ? (
                          <i className={formData.icon_url} style={{ color: formData.style.color }}></i>
                        ) : formData.icon_url && formData.icon_url.startsWith('http') ? (
                          <img src={formData.icon_url} alt="icon" className="w-10 h-10" />
                        ) : formData.icon_url ? (
                          <span>{formData.icon_url}</span>
                        ) : (
                          <span style={{ color: formData.style.color }}>📍</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Σειρά Εμφάνισης
                </label>
                <input
                  type="number"
                  value={formData.display_order}
                  onChange={(e) => setFormData({ ...formData, display_order: e.target.value })}
                  min="1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Icon Selection */}
            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Εικονίδιο (Icon) - Προαιρετικό
              </label>
              
              <div className="space-y-3">
                <div className="flex gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, icon_type: 'emoji', icon_url: '' })}
                    className={`px-4 py-2 rounded text-sm ${formData.icon_type === 'emoji' ? 'bg-blue-500 text-white' : 'bg-white text-gray-700 border'}`}
                  >
                    😀 Emoji
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, icon_type: 'fontawesome', icon_url: '' })}
                    className={`px-4 py-2 rounded text-sm ${formData.icon_type === 'fontawesome' ? 'bg-blue-500 text-white' : 'bg-white text-gray-700 border'}`}
                  >
                    <i className="fab fa-font-awesome"></i> FontAwesome
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, icon_type: 'flaticon', icon_url: '' })}
                    className={`px-4 py-2 rounded text-sm ${formData.icon_type === 'flaticon' ? 'bg-blue-500 text-white' : 'bg-white text-gray-700 border'}`}
                  >
                    🎨 Flaticon
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, icon_type: 'url', icon_url: '' })}
                    className={`px-4 py-2 rounded text-sm ${formData.icon_type === 'url' ? 'bg-blue-500 text-white' : 'bg-white text-gray-700 border'}`}
                  >
                    🔗 URL
                  </button>
                </div>

                {formData.icon_type === 'emoji' && (
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">
                      Επιλέξτε ή πληκτρολογήστε emoji:
                    </label>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {['📍', '🚇', '🚂', '🚆', '🚊', '🚌', '⚡', '🅿️', '🚻', '💧', '⛑️', '🍽️', 'ℹ️', '🏁', '🟢', '🏆', '📌', '🎯', '⭐', '🔵'].map(emoji => (
                        <button
                          key={emoji}
                          type="button"
                          onClick={() => setFormData({ ...formData, icon_url: emoji })}
                          className={`text-2xl p-2 rounded hover:bg-blue-100 ${formData.icon_url === emoji ? 'bg-blue-200' : 'bg-white border'}`}
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                    <input
                      type="text"
                      value={formData.icon_url}
                      onChange={(e) => setFormData({ ...formData, icon_url: e.target.value })}
                      placeholder="Ή πληκτρολογήστε emoji..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-2xl text-center"
                    />
                  </div>
                )}

                {formData.icon_type === 'fontawesome' && (
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">
                      FontAwesome Icon Class:
                    </label>
                    <p className="text-xs text-gray-500 mb-2">
                      Browse icons at <a href="https://fontawesome.com/icons" target="_blank" rel="noopener" className="text-blue-600 underline">fontawesome.com/icons</a>
                    </p>
                    
                    {/* Popular FontAwesome Icons */}
                    <div className="mb-3">
                      <p className="text-xs text-gray-600 mb-2">Popular icons:</p>
                      <div className="flex flex-wrap gap-2">
                        {[
                          { class: 'fa-solid fa-location-dot', label: 'Marker' },
                          { class: 'fa-solid fa-map-pin', label: 'Pin' },
                          { class: 'fa-solid fa-train', label: 'Train' },
                          { class: 'fa-solid fa-bus', label: 'Bus' },
                          { class: 'fa-solid fa-subway', label: 'Metro' },
                          { class: 'fa-solid fa-bicycle', label: 'Bike' },
                          { class: 'fa-solid fa-car', label: 'Car' },
                          { class: 'fa-solid fa-square-parking', label: 'Parking' },
                          { class: 'fa-solid fa-hospital', label: 'Medical' },
                          { class: 'fa-solid fa-utensils', label: 'Food' },
                          { class: 'fa-solid fa-droplet', label: 'Water' },
                          { class: 'fa-solid fa-circle-info', label: 'Info' },
                          { class: 'fa-solid fa-flag-checkered', label: 'Finish' },
                          { class: 'fa-solid fa-star', label: 'Star' },
                          { class: 'fa-solid fa-heart', label: 'Heart' },
                          { class: 'fa-solid fa-bolt', label: 'Electric' }
                        ].map(icon => (
                          <button
                            key={icon.class}
                            type="button"
                            onClick={() => setFormData({ ...formData, icon_url: icon.class })}
                            className={`px-3 py-2 rounded hover:bg-blue-100 flex items-center gap-2 ${formData.icon_url === icon.class ? 'bg-blue-200 border-2 border-blue-500' : 'bg-white border'}`}
                            title={icon.label}
                          >
                            <i className={icon.class}></i>
                            <span className="text-xs">{icon.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    <input
                      type="text"
                      value={formData.icon_url}
                      onChange={(e) => setFormData({ ...formData, icon_url: e.target.value })}
                      placeholder="e.g., fa-solid fa-location-dot"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                    />
                    {formData.icon_url && (
                      <div className="mt-2 p-3 bg-white rounded border">
                        <p className="text-xs text-gray-600 mb-2">Προεπισκόπηση:</p>
                        <i className={`${formData.icon_url} text-3xl`} style={{ color: formData.style.color }}></i>
                      </div>
                    )}
                  </div>
                )}

                {formData.icon_type === 'flaticon' && (
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">
                      Flaticon Class:
                    </label>
                    <p className="text-xs text-gray-500 mb-2">
                      Browse icons at <a href="https://www.flaticon.com/" target="_blank" rel="noopener" className="text-blue-600 underline">flaticon.com</a> and copy the class name
                    </p>
                    <input
                      type="text"
                      value={formData.icon_url}
                      onChange={(e) => setFormData({ ...formData, icon_url: e.target.value })}
                      placeholder="e.g., fi-rr-marker or flaticon-map-marker"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                    />
                    <p className="text-xs text-gray-500 mt-2">
                      Note: You need to include the Flaticon CSS file in your project for the icons to display.
                    </p>
                    {formData.icon_url && (
                      <div className="mt-2 p-3 bg-white rounded border">
                        <p className="text-xs text-gray-600 mb-2">Προεπισκόπηση:</p>
                        <i className={`${formData.icon_url} text-3xl`} style={{ color: formData.style.color }}></i>
                      </div>
                    )}
                  </div>
                )}

                {formData.icon_type === 'url' && (
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">
                      URL εικονιδίου (.png, .svg, .jpg):
                    </label>
                    <input
                      type="url"
                      value={formData.icon_url}
                      onChange={(e) => setFormData({ ...formData, icon_url: e.target.value })}
                      placeholder="https://example.com/icon.png"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                    {formData.icon_url && formData.icon_url.startsWith('http') && (
                      <div className="mt-2 p-2 bg-white rounded border">
                        <p className="text-xs text-gray-600 mb-1">Προεπισκόπηση:</p>
                        <img 
                          src={formData.icon_url} 
                          alt="Icon preview" 
                          className="w-8 h-8 object-contain"
                          onError={(e) => e.target.style.display = 'none'}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  GeoJSON Geometry *
                </label>
                <div className="flex gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".geojson,.json"
                    onChange={handleImportGeoJSON}
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="flex items-center gap-2 px-3 py-1 text-sm bg-green-50 text-green-700 rounded hover:bg-green-100"
                  >
                    <Upload size={16} />
                    Import GeoJSON
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowMapPreview(!showMapPreview)}
                    className="flex items-center gap-2 px-3 py-1 text-sm bg-blue-50 text-blue-700 rounded hover:bg-blue-100"
                  >
                    <MapPin size={16} />
                    {showMapPreview ? 'Απόκρυψη' : 'Προεπισκόπηση'} Χάρτη
                  </button>
                </div>
              </div>
              <textarea
                value={formData.geometry}
                onChange={(e) => setFormData({ ...formData, geometry: e.target.value })}
                onBlur={(e) => parseGeoJSON(e.target.value)}
                rows={10}
                placeholder='{"type":"LineString","coordinates":[[lon,lat],[lon,lat],...]}'
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Βάλτε GeoJSON geometry (LineString, Polygon, Point κλπ.)
              </p>

              {/* Map Preview */}
              {showMapPreview && (() => {
                const previewGeometry = getPreviewGeometry();
                if (!previewGeometry) {
                  return (
                    <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-800">
                      Μη έγκυρο GeoJSON. Διορθώστε το geometry για προεπισκόπηση.
                    </div>
                  );
                }

                return (
                  <div className="mt-4 h-96 rounded-lg overflow-hidden border-2 border-blue-300">
                    <MapContainer
                      center={[37.9838, 23.7275]}
                      zoom={13}
                      style={{ height: '100%', width: '100%' }}
                    >
                      <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      />
                      <FitBounds geometry={previewGeometry} />
                      
                      {previewGeometry.type === 'LineString' && (
                        <Polyline
                          positions={previewGeometry.coordinates.map(coord => [coord[1], coord[0]])}
                          color={formData.style.color}
                          weight={formData.style.weight}
                          opacity={formData.style.opacity}
                        />
                      )}
                      
                      {previewGeometry.type === 'Polygon' && (
                        <Polyline
                          positions={previewGeometry.coordinates[0].map(coord => [coord[1], coord[0]])}
                          color={formData.style.color}
                          weight={formData.style.weight}
                          opacity={formData.style.opacity}
                        />
                      )}
                      
                      {previewGeometry.type === 'Point' && (
                        <Marker position={[previewGeometry.coordinates[1], previewGeometry.coordinates[0]]}>
                          <Popup>{formData.name || 'Preview Point'}</Popup>
                        </Marker>
                      )}
                    </MapContainer>
                  </div>
                );
              })()}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Metadata (JSON)
              </label>
              <textarea
                value={formData.metadata}
                onChange={(e) => setFormData({ ...formData, metadata: e.target.value })}
                rows={5}
                placeholder='{"vehicle_type": "bus", "direction": "from_athens"}'
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm"
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_visible"
                checked={formData.is_visible}
                onChange={(e) => setFormData({ ...formData, is_visible: e.target.checked })}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="is_visible" className="ml-2 text-sm font-medium text-gray-700">
                Ορατό στο χάρτη
              </label>
            </div>

            <div className="flex gap-4 pt-4 border-t">
              <button
                type="submit"
                className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition"
              >
                {editingLayer ? 'Ενημέρωση' : 'Δημιουργία'}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="bg-gray-300 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-400 transition"
              >
                Ακύρωση
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Layers List */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b">
          <h3 className="text-xl font-bold">Layers ({layers.length})</h3>
        </div>

        {loading ? (
          <div className="p-12 text-center text-gray-500">
            Φόρτωση...
          </div>
        ) : layers.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            Δεν υπάρχουν layers. Δημιουργήστε το πρώτο layer!
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {layers
              .sort((a, b) => a.display_order - b.display_order)
              .map((layer) => (
                <div key={layer.id} className="p-4 hover:bg-gray-50 transition">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4 flex-1">
                      {layer.icon_url && (
                        <div className="w-10 h-10 flex items-center justify-center text-2xl">
                          {layer.icon_url.startsWith('fa-') ? (
                            <i className={layer.icon_url}></i>
                          ) : layer.icon_url.startsWith('fi-') ? (
                            <i className={layer.icon_url}></i>
                          ) : layer.icon_url.startsWith('http') ? (
                            <img src={layer.icon_url} alt="" className="w-8 h-8 object-contain" />
                          ) : (
                            <span>{layer.icon_url}</span>
                          )}
                        </div>
                      )}
                      {/* Style Preview */}
                      <div className="w-20 h-12 flex items-center justify-center">
                        {layer.layer_type === 'route' && (
                          <svg width="80" height="40" className="overflow-visible">
                            <line
                              x1="5"
                              y1="20"
                              x2="75"
                              y2="20"
                              stroke={layer.style.color}
                              strokeWidth={Math.min(layer.style.weight, 6)}
                              strokeOpacity={layer.style.opacity}
                              strokeDasharray={layer.style.dashArray || 'none'}
                              strokeLinecap={layer.style.lineCap || 'round'}
                            />
                          </svg>
                        )}
                        {layer.layer_type === 'polygon' && (
                          <svg width="40" height="40">
                            <rect
                              x="5"
                              y="5"
                              width="30"
                              height="30"
                              fill={layer.style.fillColor || layer.style.color}
                              fillOpacity={layer.style.fillOpacity || 0.2}
                              stroke={layer.style.color}
                              strokeWidth={Math.min(layer.style.weight, 3)}
                              strokeOpacity={layer.style.opacity}
                            />
                          </svg>
                        )}
                        {layer.layer_type === 'point' && !layer.icon_url && (
                          <div style={{ color: layer.style.color, fontSize: '24px' }}>
                            📍
                          </div>
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <h4 className="font-semibold text-gray-900">{layer.name}</h4>
                          <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                            {layer.layer_type}
                          </span>
                          {!layer.is_visible && (
                            <span className="px-2 py-0.5 bg-red-100 text-red-600 text-xs rounded">
                              Κρυφό
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-gray-500 mt-1">
                          <span>Σειρά: {layer.display_order}</span>
                          {' • '}
                          <span>Πάχος: {layer.style.weight}px</span>
                          {' • '}
                          <span>Διαφάνεια: {layer.style.opacity}</span>
                          {layer.style.dashArray && (
                            <>
                              {' • '}
                              <span>Μοτίβο: {layer.style.dashArray}</span>
                            </>
                          )}
                          {layer.style.lineCap && layer.style.lineCap !== 'round' && (
                            <>
                              {' • '}
                              <span>Άκρα: {layer.style.lineCap}</span>
                            </>
                          )}
                          {layer.geometry?.coordinates && (
                            <>
                              {' • '}
                              <span>{layer.geometry.coordinates.length} σημεία</span>
                            </>
                          )}
                        </div>
                        {layer.metadata && Object.keys(layer.metadata).length > 0 && (
                          <div className="text-xs text-gray-400 mt-1">
                            {Object.entries(layer.metadata).map(([key, value]) => (
                              <span key={key} className="mr-3">
                                {key}: {value}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleExportGeoJSON(layer)}
                        className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded"
                        title="Export GeoJSON"
                      >
                        <Download size={18} />
                      </button>
                      <button
                        onClick={() => toggleVisibility(layer)}
                        className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded"
                        title={layer.is_visible ? 'Απόκρυψη' : 'Εμφάνιση'}
                      >
                        {layer.is_visible ? <Eye size={18} /> : <EyeOff size={18} />}
                      </button>
                      <button
                        onClick={() => handleEdit(layer)}
                        className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded"
                        title="Επεξεργασία"
                      >
                        <Edit2 size={18} />
                      </button>
                      <button
                        onClick={() => handleDelete(layer)}
                        className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded"
                        title="Διαγραφή"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  );
}
